import {Component, inject} from '@angular/core';
import {Router, RouterOutlet} from '@angular/router';
import {FormsModule} from '@angular/forms';
import {AdminApiService} from '../../admin-api.service';
import {AdminAccessState, AdminUser} from '../../admin.models';
import {AdminTopNavComponent} from '../../shared/admin-top-nav/admin-top-nav.component';
import {AdminTranslatePipe} from '../../core/i18n/admin-translate.pipe';
import {UiButtonComponent, UiCardComponent, UiFooterComponent, UiToastItem, UiToastOutletComponent, UiToastService} from 'freyr-ui';
import {BuildVersionInfo, BuildVersionService} from '../../core/services/build-version.service';

@Component({
  selector: 'app-admin-shell',
  standalone: true,
  imports: [RouterOutlet, UiCardComponent, UiButtonComponent, UiFooterComponent, AdminTopNavComponent, UiToastOutletComponent, AdminTranslatePipe, FormsModule],
  templateUrl: './admin-shell.component.html',
  styleUrl: './admin-shell.component.scss'
})
export class AdminShellComponent {
  readonly api = inject(AdminApiService);
  private readonly router = inject(Router);
  private readonly toastService = inject(UiToastService);
  private readonly buildVersionService = inject(BuildVersionService);

  access: AdminAccessState = { state: 'checking', currentUser: null };
  toasts: UiToastItem[] = [];
  availableUpdate: BuildVersionInfo | null = null;
  monitoringUsername = '';
  monitoringPassword = '';
  monitoringLoading = false;
  monitoringError = '';

  constructor() {
    this.toastService.toasts$.subscribe((toasts: UiToastItem[]) => {
      this.toasts = toasts;
    });
    this.buildVersionService.availableUpdate$.subscribe((buildInfo) => {
      this.availableUpdate = buildInfo;
    });
    this.bootstrap();
  }

  isActive(routePrefix: string): boolean {
    return this.router.url.startsWith(routePrefix);
  }

  go(path: string): void {
    void this.router.navigateByUrl(path);
  }

  dismissToast(id: number): void {
    this.toastService.dismiss(id);
  }

  refreshForUpdate(): void {
    this.buildVersionService.reloadPage();
  }

  get monitoringMode(): boolean {
    return this.access.state === 'monitoring';
  }

  get canRenderRoute(): boolean {
    return this.access.state === 'granted' || (this.access.state === 'monitoring' && this.router.url.startsWith('/overview'));
  }

  goToOverview(): void {
    void this.router.navigateByUrl('/overview');
  }

  get updateBannerVersion(): string {
    return this.availableUpdate?.version ?? '';
  }

  get updateBannerBuildId(): string {
    return this.availableUpdate?.buildId ?? '';
  }

  enterMonitoringMode(): void {
    this.monitoringLoading = true;
    this.monitoringError = '';
    this.api.setMonitoringCredentials(this.monitoringUsername.trim(), this.monitoringPassword).subscribe({
      next: (result) => {
        this.monitoringLoading = false;
        this.activateMonitoringMode(result.username);
      },
      error: () => {
        this.api.clearMonitoringCredentials();
        this.monitoringLoading = false;
        this.monitoringError = 'admin.shell.monitoringLoginError';
      }
    });
  }

  private bootstrap(): void {
    this.api.refreshSession().subscribe({
      next: () => this.loadCurrentUser(),
      error: () => this.loadCurrentUser()
    });
  }

  private loadCurrentUser(): void {
    this.api.getCurrentUser().subscribe({
      next: (user) => {
        const roles = user?.roles ?? [];
        const hasStaffAccess = roles.includes('ADMIN') || roles.includes('SUPPORT');
        this.access = hasStaffAccess
          ? { state: 'granted', currentUser: user }
          : { state: 'forbidden', currentUser: user };
        if (hasStaffAccess && roles.includes('SUPPORT') && !roles.includes('ADMIN')) {
          const path = this.router.url.split('?')[0];
          if (path === '/' || path === '/overview' || path.endsWith('/overview')) {
            void this.router.navigateByUrl('/users');
          }
        }
      },
      error: (error) => {
        if (error.status === 401) {
          this.access = { state: 'unauthenticated', currentUser: null };
          return;
        }
        this.tryMonitoringFallback();
      }
    });
  }

  private tryMonitoringFallback(): void {
    if (!this.api.isMonitoringMode()) {
      this.access = { state: 'monitoringLogin', currentUser: null };
      return;
    }
    this.api.checkMonitoringAccess().subscribe({
      next: (result) => this.activateMonitoringMode(result.username),
      error: () => {
        this.api.clearMonitoringCredentials();
        this.access = { state: 'monitoringLogin', currentUser: null };
      }
    });
  }

  private activateMonitoringMode(username: string): void {
    const user: AdminUser = {
      id: 'monitoring',
      username,
      roles: ['MONITORING']
    };
    this.access = { state: 'monitoring', currentUser: user };
    if (!this.router.url.startsWith('/overview')) {
      void this.router.navigateByUrl('/overview');
    }
  }
}

